import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import {
  User, GraduationCap, BookOpen, Activity, Users, FolderOpen,
  Calendar, ChevronRight, ArrowRight, FileText, Mic, Search, Clock, ClipboardCheck,
  TrendingUp, DollarSign, Zap
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import UpsellPrompt from '../components/UpsellPrompt';

function ProgressRing({ pct, size = 48, stroke = 4, color = '#0F392B' }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F2F2F0" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-700" />
    </svg>
  );
}

function SectionCard({ icon: Icon, label, pct, detail, link, color, children }) {
  return (
    <Link to={link} className="block" data-testid={`section-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Card className="border-[#E2E2E2] hover:border-[#0F392B]/20 hover:shadow-sm transition-all group">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <ProgressRing pct={pct} size={48} color={color || '#0F392B'} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] font-bold">{pct}%</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">{label}</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
            {children}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[#0F392B] transition-colors" />
        </CardContent>
      </Card>
    </Link>
  );
}

function MyListStats({ myList }) {
  if (!myList || !myList.total) return null;
  const tiles = [
    {
      icon: TrendingUp,
      label: 'Avg acceptance rate',
      value: myList.avg_acceptance_rate != null ? `${myList.avg_acceptance_rate}%` : '—',
      sub: myList.rates_counted ? `across ${myList.rates_counted} of ${myList.total} schools` : 'add schools with data',
      testid: 'my-list-avg-acceptance',
    },
    {
      icon: DollarSign,
      label: 'Total application fees',
      value: myList.total_fees_us != null ? `$${myList.total_fees_us.toLocaleString()}` : '—',
      sub: myList.fees_counted
        ? `${myList.fees_counted} synced${myList.free_apps ? ` · ${myList.free_apps} free` : ''}`
        : 'auto-syncs from the grid',
      testid: 'my-list-total-fees',
    },
    {
      icon: Zap,
      label: 'Offer Early Decision',
      value: myList.ed_count,
      sub: 'ED / EDII plans on your list',
      testid: 'my-list-ed-count',
    },
    {
      icon: Clock,
      label: 'Offer Early Action',
      value: myList.ea_count,
      sub: 'EA / REA plans on your list',
      testid: 'my-list-ea-count',
    },
  ];
  return (
    <div className="space-y-2" data-testid="my-list-stats">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
          My List Intelligence
        </h2>
        <Link to="/colleges" className="text-xs text-[#0F392B] hover:underline flex items-center gap-1" data-testid="my-list-stats-open">
          Open list <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiles.map((t) => (
          <Card key={t.testid} className="border-[#E2E2E2]" data-testid={t.testid}>
            <CardContent className="p-3.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <t.icon className="h-3.5 w-3.5 text-[#D4AF37]" />
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium leading-tight">{t.label}</p>
              </div>
              <p className="text-xl font-semibold text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.value}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{t.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDashboardStats()
      .then(setStats)
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="max-w-3xl mx-auto space-y-4 animate-pulse">
      <div className="h-24 bg-[#E2E2E2] rounded-xl" />
      {[1,2,3,4,5].map(i => <div key={i} className="h-20 bg-[#E2E2E2] rounded-xl" />)}
    </div>
  );

  if (!stats) return null;

  const s = stats.sections || {};
  const readiness = stats.overall_readiness || 0;
  const firstName = stats.user?.first_name || user?.first_name || 'Student';

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="dashboard-page">
      {/* Readiness Header */}
      <div className="flex items-center gap-6">
        <div className="relative flex-shrink-0">
          <ProgressRing pct={readiness} size={80} stroke={6}
            color={readiness >= 75 ? '#16a34a' : readiness >= 50 ? '#D4AF37' : readiness >= 25 ? '#f59e0b' : '#ef4444'} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold">{readiness}%</span>
          </div>
        </div>
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Welcome back, {firstName}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {readiness >= 75 ? "Looking strong! Keep refining your application."
              : readiness >= 50 ? "Good progress. Focus on the sections below to strengthen your application."
              : readiness >= 25 ? "You're building momentum. Complete the highlighted sections next."
              : "Let's get started! Begin with your Profile and add some schools."}
          </p>
        </div>
      </div>

      {/* Contextual Upsell — placed where users naturally see it after the readiness header */}
      {readiness < 50 ? (
        <UpsellPrompt context="dashboard-low-readiness" readiness={readiness} />
      ) : readiness < 75 ? (
        <UpsellPrompt context="dashboard-mid-readiness" readiness={readiness} />
      ) : null}
      {(s.schools?.total || 0) === 0 && readiness >= 50 && (
        <UpsellPrompt context="school-list-empty" />
      )}

      {/* My List Intelligence */}
      <MyListStats myList={stats.my_list} />

      {/* Section Progress Cards */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
          Application Progress
        </h2>
        <SectionCard icon={User} label="Profile" pct={s.profile?.pct || 0} link="/profile"
          detail={`${Object.values(s.profile?.breakdown || {}).reduce((a, b) => a + b.filled, 0)} of ${Object.values(s.profile?.breakdown || {}).reduce((a, b) => a + b.total, 0)} fields complete`}>
          <div className="flex gap-1.5 mt-1.5">
            {Object.entries(s.profile?.breakdown || {}).map(([k, v]) => (
              <Badge key={k} variant="outline" className={`text-[9px] capitalize ${v.pct === 100 ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : ''}`}>
                {k} {v.pct}%
              </Badge>
            ))}
          </div>
        </SectionCard>

        <SectionCard icon={GraduationCap} label="Schools" pct={s.schools?.pct || 0} link="/colleges"
          detail={`${s.schools?.total || 0} schools added`}>
          {s.schools?.total > 0 && (
            <div className="flex gap-1.5 mt-1.5">
              {Object.entries(s.schools?.by_type || {}).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[9px] capitalize">{k}: {v}</Badge>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={Activity} label="Activities" pct={s.activities?.pct || 0} link="/activities"
          color={s.activities?.pct === 100 ? '#16a34a' : '#0F392B'}
          detail={`${s.activities?.filled || 0}/10 slots · ${s.activities?.complete || 0} complete · ${(s.activities?.total_hours || 0).toLocaleString()} hrs/yr`} />

        <SectionCard icon={BookOpen} label="Writing" pct={s.essays?.pct || 0} link="/essays"
          detail={`${s.essays?.total || 0} essays · ${s.essays?.finalized || 0} finalized · ${(s.essays?.total_words || 0).toLocaleString()} words`}>
          {(s.essays?.total || 0) > 0 && (
            <div className="flex gap-1.5 mt-1.5">
              <Badge variant="outline" className="text-[9px]">Personal: {s.essays?.personal || 0}</Badge>
              <Badge variant="outline" className="text-[9px]">Supplemental: {s.essays?.supplemental || 0}</Badge>
            </div>
          )}
        </SectionCard>

        <SectionCard icon={Users} label="Recommendations" pct={s.recommendations?.pct || 0} link="/recommenders"
          detail={`${s.recommendations?.total || 0} recommenders · ${s.recommendations?.submitted || 0} submitted`}>
          <div className="flex gap-1.5 mt-1.5">
            <Badge variant="outline" className={`text-[9px] ${(s.recommendations?.counselors || 0) >= 1 ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : ''}`}>
              Counselor: {s.recommendations?.counselors || 0}
            </Badge>
            <Badge variant="outline" className={`text-[9px] ${(s.recommendations?.teachers || 0) >= 2 ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : ''}`}>
              Teachers: {s.recommendations?.teachers || 0}/2
            </Badge>
          </div>
        </SectionCard>

        <SectionCard icon={FolderOpen} label="Documents" pct={Math.min((s.documents?.total || 0) * 25, 100)} link="/documents"
          detail={`${s.documents?.total || 0} documents · ${s.documents?.with_files || 0} with files`} />
      </div>

      {/* Quick Actions */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
          Tools
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[
            { icon: Search, label: 'School Explorer', to: '/school-explorer' },
            { icon: Calendar, label: 'Deadlines', to: '/timeline' },
            { icon: FileText, label: 'Resume', to: '/resume' },
            { icon: Mic, label: 'Interview Prep', to: '/interview' },
            { icon: ClipboardCheck, label: 'Checklist', to: '/checklist' },
          ].map(t => (
            <Link key={t.to} to={t.to} data-testid={`quick-${t.label.toLowerCase().replace(/\s/g, '-')}`}>
              <Card className="border-[#E2E2E2] hover:border-[#0F392B]/20 hover:shadow-sm transition-all">
                <CardContent className="p-3 flex items-center gap-2">
                  <t.icon className="h-4 w-4 text-[#0F392B]" />
                  <span className="text-xs font-medium">{t.label}</span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Interview Stats (if practiced) */}
      {stats.interview?.practiced > 0 && (
        <Card className="border-[#E2E2E2]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#0F392B]/5 flex items-center justify-center">
              <Mic className="h-5 w-5 text-[#0F392B]" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium">Interview Practice</h3>
              <p className="text-xs text-muted-foreground">
                {stats.interview.practiced} questions practiced · Avg score: {stats.interview.avg_score}/10
              </p>
            </div>
            <Link to="/interview" className="text-xs text-[#0F392B] hover:underline flex items-center gap-1">
              Continue <ArrowRight className="h-3 w-3" />
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
